import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { clearLeaveGuard, setLeaveGuard, type LeaveGuard } from '../../app/leave-guard';
import { useFullScreenPage } from '../../app/full-screen';
import { Dialog, DialogActions } from '../../design-system/Overlay';
import { Icon } from '../../design-system/icons';
import type { CanvasNode, InsertResult, StudioShellProps, VersionRow } from './canvas-types';
import { DeviceFrame, fitScale, rotated } from './DeviceFrame';
import type { ApprovalAnswer, Finding, PreviewFormat } from './editor-types';
import { canRedo, canUndo, commit, redo, startHistory, undo, type EditorHistory } from './history';
import { editorShortcut, SHORTCUT_HELP, type EditorCommand } from './shortcuts';
import { CommandPalette, HistoryDialog, ProposeDialog, PublishDialog, VerifyDialog, type PaletteCommand } from './StudioDialogs';
import { useStudioDraft } from './use-studio-draft';
import { IconButton, InspectorPanel, LAYERS_HELP, LayersPanel, LibraryPanel, PanelHead, Popover, SelectionBar, useFirstUseHint } from './StudioPanels';
import { studioTypeVariables } from './studio-theme';
import './studio-shell.css';

/**
 * Coquille des studios sur canevas (ADR-145 §11, ADR-146 §5), plein écran hors de la coquille de l'application :
 * barre (retour, sujet, état de l'enregistrement, couche ; appareil et état d'aperçu ; annuler, rétablir,
 * historique, bouton principal, menu ⋯), rail de panneaux, canevas rendu par le renderer réel du métier, zoom en
 * bas, inspecteur à onglets. Studios en sombre (mêmes jetons, mode `dark`), aperçu aux couleurs de la cible.
 *
 * Finition (ADR-146, addendum « finition de l'éditeur ») : la page ne défile jamais, chaque zone défile seule ;
 * boutons à icône avec infobulle et raccourci ; aucune explication affichée en permanence — la couche et les
 * avertissements sont des pastilles qui s'ouvrent, l'aide « cliquez un élément » ne sert qu'au premier usage.
 */

/** Compléments de la coquille, propres à la finition : ils restent facultatifs pour tous les profils. */
export interface StudioShellExtras {
  /** Ce que veut dire la couche (« vos modifications forment une surcharge… »), ouvert depuis la pastille. */
  layerHelp?: ReactNode;
  /** Libellé court de la pastille qui ouvre `notice` (« Brouillon d’une autre version »). */
  noticeLabel?: string;
  /** Mode développeur (noms internes) : une case du menu ⋯ de la barre, jamais un onglet de l'inspecteur. */
  developer?: { on: boolean; onChange: (on: boolean) => void };
}

type Panel = 'add' | 'layers' | string | null;

/** Largeur de fenêtre sous laquelle le panneau de gauche est fermé d'office : la place va à l'aperçu. */
const WIDE = 1500;
const TYPE = studioTypeVariables();
/** Icônes du registre par appareil et par panneau ; un profil inconnu garde son tracé ou son glyphe. */
const DEVICE_ICONS: Readonly<Record<string, string>> = { tablet: 'tablet', mobile: 'mobile', web: 'monitor' };
const PANEL_ICONS: Readonly<Record<string, string>> = { add: 'plus', layers: 'layers', steps: 'rows' };
/** Place réservée en haut de la scène pour la barre flottante (px) : la marge haute de la scène moins sa marge de base. */
const STAGE_TOP_RESERVE = 44;

function flatten(node: CanvasNode, out: CanvasNode[] = []): CanvasNode[] {
  out.push(node);
  for (const child of node.children) flatten(child, out);
  return out;
}

/** Fond du canevas : la surface creusée du thème sombre, posée sous un nom propre pour ne pas assombrir l'aperçu. */
function stageVariables(chrome: Readonly<Record<string, string>> | undefined): Record<string, string> {
  if (chrome === undefined) return {};
  const out: Record<string, string> = {};
  const sunken = chrome['--ay-color-surface-sunken'];
  const dot = chrome['--ay-color-border-default'];
  if (sunken !== undefined) out['--ay-studio-stage-bg'] = sunken;
  if (dot !== undefined) out['--ay-studio-stage-dot'] = dot;
  return out;
}

export function StudioShell<D>({ title, layer, layerHelp, base, initial, profile, port, history: historyPort, onPublished, notice, noticeLabel, chrome, onClose, drafts, savedDraft, developer }: StudioShellProps<D> & StudioShellExtras) {
  const [history, setHistory] = useState<EditorHistory<D>>(() => startHistory(initial));
  const [selected, setSelected] = useState<string>('');
  const [panel, setPanel] = useState<Panel>(() => (typeof window !== 'undefined' && window.innerWidth >= WIDE ? 'add' : null));
  const [libraryTarget, setLibraryTarget] = useState<string | null>(null);
  // Version remise dans le brouillon par « Historique › Restaurer » : la pastille de couche la nomme, au lieu de la
  // version ouverte, tant que le brouillon en part.
  const [restored, setRestored] = useState<VersionRow | null>(null);
  const shownLayer = restored === null ? layer : `${layer} → ${restored.name}`;
  const [refusal, setRefusal] = useState<string | null>(null);
  const [announce, setAnnounce] = useState('');
  const [formatId, setFormatId] = useState(profile.formats[0]?.id ?? 'web');
  const [previewState, setPreviewState] = useState(profile.states[0]?.id ?? 'filled');
  const [zones, setZones] = useState(false);
  const [showHidden, setShowHidden] = useState(true);
  const [trying, setTrying] = useState(false);
  const [dialog, setDialog] = useState<'palette' | 'propose' | 'publish' | 'history' | 'verify' | null>(null);
  // Groupes de l'inspecteur ouverts ou repliés par l'auteur : gardés d'un élément à l'autre.
  const [groups, setGroups] = useState<Readonly<Record<string, boolean>>>({});
  const [selectHint, dismissSelectHint] = useFirstUseHint('select');
  const searchRef = useRef<HTMLInputElement>(null);
  const draft = history.present;
  useFullScreenPage();
  const persistence = useStudioDraft(drafts, draft, { document: initial, ...(savedDraft === undefined ? {} : { version: savedDraft.version, savedAt: savedDraft.savedAt }) });
  // Publier selon la politique du tenant : demandé au serveur pour ce changement (structurel ou non).
  const [access, setAccess] = useState<ApprovalAnswer | null>(null);
  // Aperçu « Ajuster » : l'appareil prend la largeur et la hauteur du canevas, proportions gardées ; un zoom
  // choisi l'emporte (100 % = taille réelle de l'appareil, en points CSS). « Ajuster » replie aussi la bibliothèque
  // et l'inspecteur : à côté d'eux, une tablette tombait à environ 0,58, textes illisibles. Le choix d'un élément
  // rouvre l'inspecteur.
  const stageRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<{ width: number; height: number } | null>(null);
  const [zoom, setZoom] = useState<number | null>(null);
  const [folded, setFolded] = useState(false);
  useEffect(() => {
    const element = stageRef.current;
    if (element === null) return undefined;
    // La scène garde en haut la place de la barre flottante de l'élément choisi (STAGE_TOP_RESERVE) : l'aperçu ajusté ne passe jamais dessous.
    const measure = (): void => setStage({ width: element.clientWidth, height: element.clientHeight - STAGE_TOP_RESERVE });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const zoomRef = useRef<(step: number) => void>(() => undefined);
  useEffect(() => {
    const element = stageRef.current;
    if (element === null) return undefined;
    const onWheel = (event: WheelEvent): void => {
      if (!(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      zoomRef.current(event.deltaY < 0 ? 0.1 : -0.1);
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => element.removeEventListener('wheel', onWheel);
  }, []);
  // Garde de sortie de l'éditeur : des modifications non proposées demandent « Quitter l'éditeur ? » (menu,
  // retour du navigateur, fil d'Ariane, Fermer) ; rien de tel pour un brouillon déjà proposé tel quel.
  const [proposed, setProposed] = useState<D | null>(null);
  const [leaving, setLeaving] = useState<(() => void) | null>(null);
  // Avec un brouillon au serveur, seule une modification non encore enregistrée retient ; sinon, une modification non proposée.
  const dirty = drafts !== undefined ? persistence.unsaved : history.past.length > 0 && !Object.is(draft, proposed);
  useEffect(() => {
    if (!dirty) return undefined;
    const guard: LeaveGuard = (next) => setLeaving(() => next);
    const onUnload = (event: BeforeUnloadEvent): void => event.preventDefault();
    setLeaveGuard(guard);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      clearLeaveGuard(guard);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [dirty]);
  const trackedPort = useMemo(() => ({ ...port, propose: async (document: D, reason: string) => { const result = await port.propose(document, reason); setProposed(document); return result; } }), [port]);
  const close = (): void => {
    if (onClose === undefined) return;
    if (dirty) setLeaving(() => onClose);
    else onClose();
  };
  const chosen: PreviewFormat = profile.formats.find((candidate) => candidate.id === formatId) ?? profile.formats[0] ?? { id: 'web', label: 'Web' };
  // Orientation choisie (segment Portrait / Paysage) ; absente : celle de l'appareil (tablette en paysage, mobile en portrait).
  const [orientation, setOrientation] = useState<'landscape' | 'portrait' | null>(null);
  const device = chosen.orientation !== undefined && orientation !== null && orientation !== chosen.orientation ? rotated(chosen) : chosen;
  const deviceIcon = chosen.orientation === undefined ? 'tablet' : (DEVICE_ICONS[chosen.id] ?? 'tablet');
  const fit = fitScale(device, stage);
  // Ouverture (décision 7) : 100 % centré si l'appareil tient dans la scène, sinon « Ajuster » — l'ajustement
  // plafonné à 100 %, sans défilement de la scène. « Ajuster » (un clic) lève le plafond ; un zoom choisi l'emporte.
  const [capped, setCapped] = useState(true);
  const scale = zoom ?? (capped ? Math.min(fit, 1) : fit);
  // Zoom et « Ajuster » (ADR-146 addendum (15)) : une transition douce, centrée sur l'élément choisi (sinon sur
  // le centre de la scène), jamais un saut. L'échelle montrée rejoint l'échelle voulue en 180 ms.
  const shown = useSmoothScale(scale, stageRef);
  const format: PreviewFormat = { ...device, scale: shown.value };
  const changeZoom = (next: number | null): void => {
    shown.animateNext();
    setZoom(next);
  };
  const zoomBy = (step: number): void => changeZoom(Math.max(0.2, Math.min(2, Math.round((scale + step) * 10) / 10)));
  zoomRef.current = zoomBy;
  /** « Ajuster » : panneaux repliés et aperçu à la place libérée ; une seconde fois, les panneaux reviennent. */
  const fitStage = (): void => {
    const fold = !(zoom === null && !capped && folded);
    if (fold) setPanel(null);
    setFolded(fold);
    setCapped(false);
    changeZoom(null);
  };

  const tree = useMemo(() => profile.tree(draft), [profile, draft]);
  const nodes = useMemo(() => flatten(tree), [tree]);
  const current = nodes.some((node) => node.id === selected && node.heading !== true) ? selected : '';
  const findings = useMemo(() => profile.localFindings(draft), [profile, draft]);
  const counts = useMemo(() => {
    const map = new Map<string, { errors: number; warnings: number }>();
    for (const finding of findings) {
      const id = profile.nodeOf(draft, finding.path);
      const entry = map.get(id) ?? { errors: 0, warnings: 0 };
      if (finding.severity === 'error') entry.errors += 1;
      else entry.warnings += 1;
      map.set(id, entry);
    }
    return map;
  }, [profile, draft, findings]);
  const estimate = useMemo(() => profile.classify(base, draft), [profile, base, draft]);
  const actions = useMemo(() => profile.actions(draft, current), [profile, draft, current]);
  const apply = useCallback((next: D, coalesce?: string): void => setHistory((state) => commit(state, next, coalesce)), []);
  const inspector = useMemo(() => profile.inspector(draft, current, apply, (id) => setSelected(id)), [profile, draft, current, apply]);
  const labelOf = (id: string): string => nodes.find((node) => node.id === id)?.label ?? '';

  const select = (id: string): void => {
    setSelected(id);
    setRefusal(null);
    if (id !== '') setFolded(false);
    if (id !== '') dismissSelectHint();
  };
  const openLibrary = (target: string | null): void => {
    setLibraryTarget(target);
    setPanel('add');
    setRefusal(null);
    window.setTimeout(() => searchRef.current?.focus(), 0);
  };
  const insert = (run: (document: D, target: string | null) => InsertResult<D>): void => {
    const result = run(draft, libraryTarget ?? actions.insertAfter ?? null);
    if ('refused' in result) {
      setRefusal(result.refused);
      return;
    }
    apply(result.document);
    select(result.id);
    setLibraryTarget(null);
    setAnnounce(`« ${labelOf(result.id) || 'Élément'} » ajouté.`);
  };
  const run = (command: EditorCommand): boolean => {
    switch (command) {
      case 'undo':
        setHistory(undo);
        return true;
      case 'redo':
        setHistory(redo);
        return true;
      case 'duplicate': {
        const copy = actions.duplicate?.();
        if (copy === undefined) return false;
        apply(copy.document);
        setSelected(copy.id);
        setAnnounce('Copie ajoutée juste après.');
        return true;
      }
      case 'remove': {
        if (actions.remove === undefined) return false;
        const name = labelOf(current);
        apply(actions.remove());
        setSelected(actions.parent ?? '');
        setAnnounce(`« ${name} » supprimé. Ctrl Z pour annuler.`);
        return true;
      }
      case 'parent':
        if (actions.parent === undefined) return false;
        setSelected(actions.parent);
        return true;
      case 'move_up':
        if (actions.moveUp === undefined) return false;
        apply(actions.moveUp());
        return true;
      case 'move_down':
        if (actions.moveDown === undefined) return false;
        apply(actions.moveDown());
        return true;
      case 'search':
        openLibrary(libraryTarget);
        return true;
      case 'palette':
        setDialog('palette');
        return true;
      case 'save':
        void persistence.saveNow();
        return drafts !== undefined;
      case 'zoom_in':
        zoomBy(0.1);
        return true;
      case 'zoom_out':
        zoomBy(-0.1);
        return true;
      case 'zoom_fit':
        fitStage();
        return true;
      case 'escape':
        if (dialog !== null || leaving !== null) return false;
        // Échap désélectionne ; sans sélection, il ferme l'éditeur (avec la garde des modifications).
        if (current === '' && libraryTarget === null) {
          close();
          return onClose !== undefined;
        }
        setSelected('');
        setLibraryTarget(null);
        return true;
    }
  };
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (dialog !== null && dialog !== 'palette') return;
    const command = editorShortcut(event, event.target);
    if (command !== null && run(command)) event.preventDefault();
  };
  // Quand le focus est perdu (élément retiré du canevas, éditeur sur place refermé, clic sur l'aperçu qui ne
  // prend pas le focus), les raccourcis de l'éditeur valent encore : la page entière est l'éditeur.
  const shortcutsRef = useRef(onKeyDown);
  shortcutsRef.current = onKeyDown;
  useEffect(() => {
    const onDocumentKey = (event: globalThis.KeyboardEvent): void => {
      if (event.defaultPrevented || (event.target !== document.body && event.target !== document.documentElement)) return;
      shortcutsRef.current(event as unknown as KeyboardEvent<HTMLElement>);
    };
    document.addEventListener('keydown', onDocumentKey);
    return () => document.removeEventListener('keydown', onDocumentKey);
  }, []);

  const paletteCommands: PaletteCommand[] = [
    { id: 'add', label: 'Ajouter un bloc ou un champ', keys: '/', run: () => openLibrary(libraryTarget) },
    { id: 'layers', label: 'Afficher les calques', run: () => setPanel('layers') },
    ...(profile.panels ?? []).map((extra) => ({ id: `panel:${extra.id}`, label: `Afficher : ${extra.label}`, run: () => setPanel(extra.id) })),
    { id: 'verify', label: 'Vérifier le brouillon', run: () => setDialog('verify') },
    { id: 'propose', label: 'Proposer la publication', run: () => setDialog('propose') },
    ...(historyPort === undefined ? [] : [{ id: 'history', label: 'Historique et comparaison', run: () => setDialog('history') }]),
    { id: 'zones', label: zones ? 'Masquer les zones' : 'Afficher les zones', run: () => setZones((value) => !value) },
    { id: 'try', label: trying ? 'Quitter le mode essai' : 'Essayer le formulaire', run: () => setTrying((value) => !value) },
    ...profile.formats.map((candidate) => ({ id: `format:${candidate.id}`, label: `Aperçu : ${candidate.label}`, run: () => setFormatId(candidate.id) })),
    ...(canUndo(history) ? [{ id: 'undo', label: 'Annuler la dernière modification', keys: 'Ctrl Z', run: () => setHistory(undo) }] : []),
    ...(canRedo(history) ? [{ id: 'redo', label: 'Rétablir', keys: 'Ctrl Maj Z', run: () => setHistory(redo) }] : []),
    ...(actions.duplicate === undefined ? [] : [{ id: 'duplicate', label: `Dupliquer « ${labelOf(current)} »`, keys: 'Ctrl D', run: () => void run('duplicate') }]),
    ...(actions.remove === undefined ? [] : [{ id: 'remove', label: `Supprimer « ${labelOf(current)} »`, keys: 'Suppr', run: () => void run('remove') }]),
  ];
  const errors = findings.filter((finding) => finding.severity === 'error').length;
  const structural = estimate.structural;
  useEffect(() => {
    if (port.publishAccess === undefined) return undefined;
    let alive = true;
    port.publishAccess({ structural, changes: [] }).then((answer) => alive && setAccess(answer), () => alive && setAccess({ can: false }));
    return () => {
      alive = false;
    };
  }, [port, structural]);
  const canPublish = access?.can === true && port.publish !== undefined;
  // Avertissements non bloquants avant de publier (langues incomplètes) : calculés à l'ouverture seulement.
  const publishWarnings = useMemo(() => (dialog === 'publish' ? (profile.publishWarnings?.(draft) ?? null) : null), [dialog, profile, draft]);
  // Publier est grisé avec son motif : rien à publier, ou des points bloquants à corriger d'abord.
  const publishBlocked = estimate.changes.length === 0 ? 'Rien à publier : le brouillon est identique à la version publiée.' : errors > 0 ? `${errors} point${errors > 1 ? 's' : ''} à corriger avant de publier (menu ⋯, « Vérifier le brouillon »).` : null;
  const changes = history.past.length;
  const locate = (finding: Finding): void => {
    setSelected(profile.nodeOf(draft, finding.path));
    setDialog(null);
  };
  const extra = (profile.panels ?? []).find((candidate) => candidate.id === panel);
  const railEntries = [{ id: 'add', label: 'Ajouter', glyph: '＋', keys: '/' as string | undefined }, { id: 'layers', label: 'Calques', glyph: '☰', keys: undefined }, ...(profile.panels ?? []).map(({ id, label, glyph }) => ({ id, label, glyph, keys: undefined }))];
  const panelLabel = panel === 'add' ? 'Ajouter' : panel === 'layers' ? 'Calques' : (extra?.label ?? '');
  const rootStyle = { ...TYPE, ...stageVariables(chrome) } as CSSProperties;

  // État de l'enregistrement, discret à côté du nom : un texte, ou un bouton quand il y a quelque chose à faire.
  const saveState = persistence.status === null ? null : (
    <span className="ay-studio__saved" role="status">
      {persistence.state === 'error' ? (
        <button type="button" className="ay-studio-pill is-danger" onClick={() => persistence.retry()} title={persistence.status}>
          <Icon name="alert" size={12} />
          Non enregistré · réessayer
        </button>
      ) : persistence.unsaved && persistence.state !== 'saving' ? (
        <button type="button" className="ay-studio__saved-button" onClick={() => void persistence.saveNow()} title="Enregistrer maintenant · Ctrl S" aria-keyshortcuts="Control+S">{persistence.status}</button>
      ) : (
        persistence.status
      )}
    </span>
  );

  return (
    <section className="ay-studio" aria-label={`Studio : ${title}`} onKeyDown={onKeyDown} style={rootStyle}>
      <header className="ay-studio__bar" role="toolbar" aria-label="Barre de l’éditeur" style={chrome}>
        <div className="ay-studio__bar-start">
          {onClose !== undefined && <IconButton icon="back" label="Retour à la liste" keys="Échap" onClick={close} className="ay-studio__back" size={18} />}
          <span className="ay-studio__subject">
            <strong className="ay-studio__title" title={title}>{title}</strong>
            {saveState}
          </span>
          {layerHelp === undefined ? (
            <span className="ay-studio-pill is-layer" data-testid="studio-layer">{shownLayer}</span>
          ) : (
            <Popover
              label="La couche de ce document"
              trigger={({ open, toggle, id }) => (
                <button type="button" className="ay-studio-pill is-layer" data-testid="studio-layer" aria-expanded={open} aria-controls={open ? id : undefined} onClick={toggle} title="Ce que veut dire la couche">
                  {shownLayer}
                  <Icon name="info" size={12} />
                </button>
              )}
            >
              {layerHelp}
            </Popover>
          )}
          {notice !== undefined && notice !== null && notice !== false && (
            <Popover
              label={noticeLabel ?? 'À lire'}
              trigger={({ open, toggle, id }) => (
                <button type="button" className="ay-studio-pill is-warning" aria-expanded={open} aria-controls={open ? id : undefined} onClick={toggle}>
                  <Icon name="alert" size={12} />
                  {noticeLabel ?? 'À lire'}
                </button>
              )}
            >
              {notice}
            </Popover>
          )}
        </div>
        <div className="ay-studio__bar-center">
          <div className="ay-studio__seg ay-studio__devices" role="radiogroup" aria-label="Appareil d’aperçu">
            {profile.formats.map((candidate) => (
              <button key={candidate.id} type="button" role="radio" aria-checked={format.id === candidate.id} aria-label={candidate.label} title={candidate.label} onClick={() => setFormatId(candidate.id)}>
                {DEVICE_ICONS[candidate.id] !== undefined ? <Icon name={DEVICE_ICONS[candidate.id]} size={16} /> : candidate.icon === undefined ? candidate.label : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d={candidate.icon} /></svg>}
              </button>
            ))}
          </div>
          {profile.formats.some((candidate) => candidate.orientation !== undefined) && (
            // Orientation : l'icône de l'appareil debout (portrait) ou couchée (paysage) ; jamais une flèche circulaire (Annuler).
            <div className="ay-studio__seg ay-studio__devices" role="radiogroup" aria-label="Orientation" aria-disabled={device.orientation === undefined || undefined}>
              {(['portrait', 'landscape'] as const).map((candidate) => {
                const label = candidate === 'portrait' ? 'Portrait' : 'Paysage';
                return (
                  <button key={candidate} type="button" role="radio" aria-checked={device.orientation === candidate} aria-label={label} title={label} disabled={device.orientation === undefined} onClick={() => setOrientation(candidate)}>
                    <Icon name={deviceIcon} size={16} {...(candidate === 'landscape' ? { rotate: 90 as const } : {})} />
                  </button>
                );
              })}
            </div>
          )}
          <label className="ay-studio__state">
            <span className="ay-studio__state-label">État</span>
            <select aria-label="État d’aperçu" value={previewState} onChange={(event) => setPreviewState(event.target.value)}>
              {profile.states.map((state) => <option key={state.id} value={state.id}>{state.label}</option>)}
            </select>
          </label>
        </div>
        <div className="ay-studio__bar-end">
          <IconButton icon="undo" label="Annuler" keys="Ctrl Z" shortcut="Control+Z" disabled={!canUndo(history)} onClick={() => setHistory(undo)} />
          <IconButton icon="redo" label="Rétablir" keys="Ctrl Maj Z" shortcut="Control+Shift+Z" disabled={!canRedo(history)} onClick={() => setHistory(redo)} />
          {historyPort !== undefined && <IconButton icon="history" label="Historique" onClick={() => setDialog('history')} />}
          <span className="ay-studio__divider" aria-hidden="true" />
          <span className="ay-visually-hidden" id="studio-publish-why">{publishBlocked ?? ''}</span>
          <button type="button" className="ay-studio__button ay-studio__button--primary" disabled={publishBlocked !== null} title={publishBlocked ?? undefined} aria-describedby={publishBlocked === null ? undefined : 'studio-publish-why'} onClick={() => setDialog(canPublish ? 'publish' : 'propose')}>
            {canPublish ? 'Publier' : 'Demander la publication'}
          </button>
          <MoreMenu onVerify={() => setDialog('verify')} {...(developer === undefined ? {} : { developer })} />
        </div>
      </header>
      <div className="ay-studio__body">
        <nav className="ay-studio__rail" aria-label="Panneaux" style={chrome}>
          {railEntries.map((entry) => {
            const icon = PANEL_ICONS[entry.id];
            const pressed = panel === entry.id;
            return (
              <button key={entry.id} type="button" className="ay-studio__rail-button" aria-pressed={pressed} aria-label={entry.label} title={entry.keys === undefined ? entry.label : `${entry.label} · ${entry.keys}`} onClick={() => setPanel((open) => (open === entry.id ? null : entry.id))}>
                {icon === undefined ? <span aria-hidden="true" className="ay-studio__glyph">{entry.glyph}</span> : <Icon name={icon} size={18} />}
              </button>
            );
          })}
          <span className="ay-studio__spacer" />
          <ShortcutsHelp />
        </nav>
        {panel !== null && (
          <aside className="ay-studio__panel" aria-label={panelLabel} style={chrome}>
            {/* « Ajouter » commence par sa recherche (planche « Panneaux-Widgets ») : le rail l'ouvre et le ferme. */}
            {panel !== 'add' && <PanelHead title={panelLabel} {...(panel === 'layers' ? { help: LAYERS_HELP } : {})} onClose={() => setPanel(null)} />}
            {panel === 'add' && <LibraryPanel entries={profile.library(draft)} groups={profile.libraryGroups} groupIcons={profile.libraryGroupIcons} searchRef={searchRef} target={libraryTarget === null ? null : labelOf(libraryTarget) || 'la zone choisie'} refusal={refusal} onInsert={(entry) => insert(entry.insert)} onClearTarget={() => setLibraryTarget(null)} />}
            {panel === 'layers' && <LayersPanel root={tree} selected={current} counts={counts} onSelect={select} onAdd={openLibrary} />}
            {extra !== undefined && <div className="ay-studio-panel__body ay-studio__scroll">{extra.render({ document: draft, apply: (next) => apply(next), selected: current, select, openLibrary, save: persistence.saveNow })}</div>}
          </aside>
        )}
        <section className="ay-studio__center" aria-label="Canevas">
          <div className="ay-studio__float">
            {profile.inlineToolbar !== true && <SelectionBar label={labelOf(current)} actions={actions} chrome={chrome} onRun={(command) => void run(command)} onApply={(next) => apply(next)} onRefused={setRefusal} onAdd={() => openLibrary(actions.insertAfter ?? null)} />}
            {current === '' && selectHint && (
              <p className="ay-studio-hint" style={chrome}>
                <span>Cliquez un élément du canevas ou des calques pour le régler.</span>
                <IconButton icon="close" label="Masquer cette aide" onClick={dismissSelectHint} className="ay-studio__icon--sm" size={12} />
              </p>
            )}
            {refusal !== null && panel !== 'add' && <p className="ay-studio__refusal" role="alert">{refusal}</p>}
          </div>
          <div className={`ay-studio__stage${zoom === null ? ' is-fit' : ' is-zoomed'}`} ref={stageRef} tabIndex={-1} aria-label={`Aperçu · ${format.label}${format.orientation === undefined ? '' : format.orientation === 'portrait' ? ', portrait' : ', paysage'} · ${Math.round(scale * 100)} %`}>
            <DeviceFrame format={format}>
              <div className={`ay-studio__canvas${zones ? ' is-zoned' : ''}${trying ? ' is-trying' : ''}`}>
                {profile.renderCanvas({ document: draft, format, state: previewState, selected: current, onSelect: select, trying, zones, showHidden, apply, onRefused: setRefusal, onAnnounce: setAnnounce, onAdd: openLibrary })}
              </div>
            </DeviceFrame>
          </div>
          <footer className="ay-studio__canvasbar" style={chrome}>
            <span className="ay-studio__zoom" role="group" aria-label="Zoom de l’aperçu">
              <IconButton icon="minus" label="Réduire l’aperçu" keys="Ctrl −" keysInName={false} onClick={() => zoomBy(-0.1)} className="ay-studio__icon--sm" size={14} />
              <output aria-live="polite">{Math.round(scale * 100)} %</output>
              <IconButton icon="plus" label="Agrandir l’aperçu" keys="Ctrl +" keysInName={false} onClick={() => zoomBy(0.1)} className="ay-studio__icon--sm" size={14} />
              <IconButton icon="fit" label="Ajuster" keys="Ctrl 0" keysInName={false} pressed={zoom === null && !capped} onClick={fitStage} className="ay-studio__icon--sm" size={14} />
              <button type="button" className="ay-studio__chip-button" aria-pressed={zoom === 1} onClick={() => changeZoom(1)} title="Taille réelle de l’appareil">100 %</button>
            </span>
            <span className="ay-studio__spacer" />
            <IconButton icon="play" label="Essayer le formulaire" pressed={trying} onClick={() => setTrying((value) => !value)} className="ay-studio__icon--sm" size={14} />
            <IconButton icon={showHidden ? 'eye' : 'eye-off'} label="Éléments masqués" pressed={showHidden} disabled={trying} onClick={() => setShowHidden((value) => !value)} className="ay-studio__icon--sm" size={14} />
            <IconButton icon="grid" label="Afficher les zones" pressed={zones} onClick={() => setZones((value) => !value)} className="ay-studio__icon--sm" size={14} />
          </footer>
        </section>
        {!folded && <InspectorPanel view={inspector} open={groups} onToggle={(id, open) => setGroups((current) => ({ ...current, [id]: open }))} chrome={chrome} />}
      </div>
      <p className="ay-visually-hidden" role="status" aria-live="polite">{announce}</p>
      <CommandPalette open={dialog === 'palette'} commands={paletteCommands} onClose={() => setDialog(null)} />
      <VerifyDialog open={dialog === 'verify'} local={findings} port={trackedPort} draft={draft} onClose={() => setDialog(null)} onLocate={locate} />
      <ProposeDialog open={dialog === 'propose'} port={trackedPort} draft={draft} estimate={estimate} localErrors={errors} onClose={() => setDialog(null)} onPublished={() => onPublished?.()} />
      <PublishDialog
        open={dialog === 'publish'}
        summary={historyPort === undefined || base === null ? null : historyPort.compare(base, draft)}
        warnings={dialog === 'publish' ? publishWarnings : null}
        changes={estimate.changes.length}
        onClose={() => setDialog(null)}
        onPublish={async (reason, fix) => {
          // « Traduire puis publier » : l'action de rattrapage passe par l'historique, puis on publie le résultat.
          const fixed = fix ? (publishWarnings?.fix?.run(draft) ?? null) : null;
          if (fixed !== null) apply(fixed);
          const document = fixed ?? draft;
          const proposal = await port.publish!(document, reason);
          if (proposal.status !== 'published') return 'La publication attend la décision d’une autre personne.';
          await drafts?.discard().catch(() => undefined);
          persistence.reset(document);
          setProposed(document);
          setDialog(null);
          setAnnounce(`Version ${proposal.publishedVersion ?? ''} publiée.`);
          onPublished?.();
          return null;
        }}
      />
      {historyPort !== undefined && <HistoryDialog open={dialog === 'history'} port={historyPort} draft={draft} onClose={() => setDialog(null)} onRestore={(document, row) => { apply(document); setRestored(row); setDialog(null); setAnnounce(`${row.name} remise dans le brouillon. Ctrl Z pour annuler.`); }} />}
      <Dialog
        title="Quitter l’éditeur ?"
        open={leaving !== null}
        onClose={() => setLeaving(null)}
        size="sm"
        footer={
          <DialogActions
            destructive={{ label: drafts === undefined ? 'Quitter sans proposer' : 'Quitter sans enregistrer', onClick: () => { const next = leaving; setLeaving(null); setLeaveGuard(null); next?.(); } }}
            cancel={{ label: 'Annuler', onClick: () => setLeaving(null) }}
            primary={{ label: 'Rester', onClick: () => setLeaving(null) }}
          />
        }
      >
        <p>{drafts === undefined ? `Vos ${changes} modification${changes > 1 ? 's' : ''} n’${changes > 1 ? 'ont' : 'a'} pas été proposée${changes > 1 ? 's' : ''} : en quittant, elles sont perdues.` : 'Vos dernières modifications ne sont pas encore enregistrées : en quittant maintenant, elles sont perdues.'} Rien n’est publié sans « Publier ».</p>
      </Dialog>
    </section>
  );
}

const ZOOM_MS = 180;

/** Mouvement réduit demandé, ou impossible à savoir (tests) : pas d'animation. */
function reducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Échelle d'aperçu animée : après un changement demandé (`animateNext`), elle rejoint la cible en {@link ZOOM_MS}
 * en gardant un point fixe — le centre de l'élément choisi, sinon le centre de la scène — ramené au centre de la
 * scène. Un autre changement (panneau ouvert, appareil changé) s'applique d'un coup.
 */
function useSmoothScale(target: number, stageRef: { current: HTMLDivElement | null }): { value: number; animateNext: () => void } {
  const [value, setValue] = useState(target);
  const animate = useRef(false);
  const anchor = useRef<{ x: number; y: number } | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const targetRef = useRef(target);
  targetRef.current = target;
  useLayoutEffect(() => {
    const from = valueRef.current;
    if (Math.abs(from - target) < 0.0005) return undefined;
    const wanted = animate.current;
    animate.current = false;
    const stage = stageRef.current;
    const screen = stage?.querySelector('.ay-device__screen') ?? null;
    if (!wanted || reducedMotion() || stage === null || screen === null) {
      anchor.current = null;
      setValue(target);
      return undefined;
    }
    const area = stage.getBoundingClientRect();
    const box = screen.getBoundingClientRect();
    const chosen = stage.closest('.ay-studio')?.querySelector('.ay-dnd-frame--selected')?.getBoundingClientRect();
    const x = chosen === undefined ? area.left + area.width / 2 : chosen.left + chosen.width / 2;
    const y = chosen === undefined ? area.top + area.height / 2 : chosen.top + Math.min(chosen.height / 2, area.height / 3);
    anchor.current = { x: (x - box.left) / from, y: (y - box.top) / from };
    const start = Date.now();
    let timer = 0;
    // Pas de 16 ms (une image) par minuterie : l'animation avance aussi quand le navigateur suspend ses images.
    const step = (): void => {
      const t = Math.min(1, (Date.now() - start) / ZOOM_MS);
      const eased = 1 - (1 - t) ** 3;
      setValue(t >= 1 ? target : from + (target - from) * eased);
      if (t < 1) timer = window.setTimeout(step, 16);
    };
    timer = window.setTimeout(step, 0);
    return () => window.clearTimeout(timer);
  }, [target, stageRef]);
  // Après chaque pas : le point fixe revient au centre de la scène (la scène défile au-delà d'« Ajuster »).
  useLayoutEffect(() => {
    const point = anchor.current;
    const stage = stageRef.current;
    const screen = stage?.querySelector('.ay-device__screen') ?? null;
    if (point === null || stage === null || screen === null) return;
    const area = stage.getBoundingClientRect();
    const box = screen.getBoundingClientRect();
    stage.scrollLeft += box.left + point.x * value - (area.left + area.width / 2);
    stage.scrollTop += box.top + point.y * value - (area.top + area.height / 2);
    if (Math.abs(value - targetRef.current) < 0.0005) anchor.current = null;
  }, [value, stageRef]);
  return { value, animateNext: () => { animate.current = true; } };
}

/** Raccourcis clavier : une aide ouverte sur demande depuis le bas du rail, jamais une ligne permanente. */
function ShortcutsHelp() {
  return (
    <Popover
      label="Raccourcis clavier"
      align="start"
      className="ay-studio-shortcuts"
      trigger={({ open, toggle, id }) => (
        <button type="button" className="ay-studio__rail-button" aria-label="Raccourcis clavier" title="Raccourcis clavier" aria-expanded={open} aria-controls={open ? id : undefined} onClick={toggle}>
          <Icon name="help" size={18} />
        </button>
      )}
    >
      <strong className="ay-studio-pop__title">Raccourcis clavier</strong>
      <dl className="ay-studio-shortcuts__list">
        {SHORTCUT_HELP.map((help) => (
          <div key={help.keys}>
            <dt><kbd>{help.keys}</kbd></dt>
            <dd>{help.text}</dd>
          </div>
        ))}
      </dl>
    </Popover>
  );
}

/** Menu ⋯ de la barre : vérifier, ouvrir l'éditeur dans un nouvel onglet (même session, même cookie). */
function MoreMenu({ onVerify, developer }: { onVerify: () => void; developer?: { on: boolean; onChange: (on: boolean) => void } }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (event: MouseEvent): void => {
      if (root.current !== null && !root.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== 'Escape' || !open) return;
    // Échap referme le menu, sans fermer l'éditeur.
    event.stopPropagation();
    setOpen(false);
  };
  return (
    <span className="ay-studio__more" ref={root} onKeyDown={onKeyDown}>
      <button type="button" className="ay-studio__icon" aria-haspopup="menu" aria-expanded={open} aria-label="Plus d’actions" title="Plus d’actions" onClick={() => setOpen((value) => !value)}>
        <Icon name="more" size={16} />
      </button>
      {open && (
        <span role="menu" className="ay-studio__menu" aria-label="Plus d’actions">
          <button type="button" role="menuitem" autoFocus onClick={() => { setOpen(false); onVerify(); }}>
            <Icon name="check" size={14} />
            Vérifier le brouillon
          </button>
          <button type="button" role="menuitem" onClick={() => { setOpen(false); window.open(window.location.href, '_blank', 'noopener'); }}>
            <Icon name="external-link" size={14} />
            Ouvrir dans un nouvel onglet
          </button>
          {developer !== undefined && (
            <button type="button" role="menuitemcheckbox" aria-checked={developer.on} title="Montre les noms internes" onClick={() => { setOpen(false); developer.onChange(!developer.on); }}>
              <Icon name={developer.on ? 'check' : 'settings'} size={14} />
              Mode développeur
            </button>
          )}
        </span>
      )}
    </span>
  );
}
